import { Link } from "react-router-dom";
import { Lightning, ArrowLeft } from "@phosphor-icons/react";

export default function Privacy() {
    return (
        <div className="min-h-screen bg-background relative">
            <header className="border-b border-white/10 px-6 md:px-12 py-4 flex justify-between items-center">
                <Link to="/" data-testid="privacy-logo" className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-electric-blue flex items-center justify-center neon-border">
                        <Lightning weight="fill" size={18} className="text-white" />
                    </div>
                    <span className="font-heading font-black tracking-tighter text-lg">
                        NEXUS<span className="text-electric-blue">.</span>LAB
                    </span>
                </Link>
                <Link
                    to="/"
                    data-testid="privacy-back-link"
                    className="label-mono text-xs hover:text-electric-blue flex items-center gap-1.5 transition-colors"
                >
                    <ArrowLeft size={12} weight="bold" /> BACK
                </Link>
            </header>

            <article className="max-w-3xl mx-auto px-6 md:px-12 py-16">
                <p className="label-mono text-electric-blue mb-3">LEGAL // PRIVACY POLICY</p>
                <h1 className="font-heading text-5xl font-black tracking-tighter mb-2">Privacy Policy</h1>
                <p className="text-muted-foreground mb-12">Effective Date: April 26, 2026</p>

                <Section title="1. Who We Are">
                    Nexus Creator Lab is operated by Nexus Creative Group LLC ("Nexus", "we", "us"). This policy explains
                    what data we collect, why we collect it, and how we protect it.
                </Section>

                <Section title="2. What We Collect">
                    <p><strong>Account information:</strong> email address, display name (callsign), and a hashed password.</p>
                    <p><strong>Payment information:</strong> handled exclusively by Stripe. We do not store credit card numbers, CVCs, or banking details on our servers. We retain only the Stripe customer/session IDs and high-level billing status (tier, paid/unpaid, lifetime flag).</p>
                    <p><strong>Activity data:</strong> scripts you generate, recipes you download, leaderboard points, posts in the Feedback Lounge, timestamps of logins.</p>
                    <p><strong>Cookies:</strong> a single authentication cookie/local-storage entry to keep you logged in, plus consent state for this banner.</p>
                </Section>

                <Section title="3. How We Use It">
                    <ul className="list-disc list-inside space-y-2">
                        <li>Provide gated access to the Recipe Vault, Growth Playbook, and Script Engine</li>
                        <li>Process Stripe payments and activate/maintain your subscription tier</li>
                        <li>Calculate leaderboard rankings and award engagement points</li>
                        <li>Send transactional emails (welcome, payment confirmations, password resets) — never marketing without separate opt-in</li>
                        <li>Detect abuse, fraud, and Terms violations</li>
                    </ul>
                </Section>

                <Section title="4. Third-Party Services We Use">
                    <ul className="list-disc list-inside space-y-2">
                        <li><strong>Stripe</strong> — payment processing</li>
                        <li><strong>Google Gemini 3 Flash</strong> — script generation (your topic input is sent to Google; do not paste sensitive data)</li>
                        <li><strong>Resend</strong> — transactional email delivery</li>
                        <li><strong>Emergent Object Storage</strong> — secure PDF hosting for Recipes and Playbooks</li>
                        <li><strong>MongoDB Atlas / Self-hosted Mongo</strong> — encrypted database storage</li>
                    </ul>
                </Section>

                <Section title="5. Data Retention">
                    Account and activity data are retained while your account is active and for 12 months after deletion (for fraud and legal compliance). Stripe billing records follow Stripe's own retention policy.
                </Section>

                <Section title="6. Your Rights">
                    <p>You can:</p>
                    <ul className="list-disc list-inside space-y-2">
                        <li>Request a copy of all personal data we hold about you</li>
                        <li>Request correction of inaccurate data</li>
                        <li>Request deletion of your account and associated data</li>
                        <li>Opt out of any future marketing emails (we do not currently send marketing)</li>
                    </ul>
                    <p className="mt-3">Send any of the above requests to <a href="mailto:contact@nexuscreatorlab.com" className="text-electric-blue">contact@nexuscreatorlab.com</a>. We respond within 30 days.</p>
                </Section>

                <Section title="7. Children">
                    Nexus Creator Lab is intended for users 18 and older. We do not knowingly collect data from minors.
                </Section>

                <Section title="8. International Transfers">
                    Our infrastructure may store and process data in the United States and other jurisdictions. By using the Lab you consent to such international transfer.
                </Section>

                <Section title="9. Changes">
                    We will post material updates here with a revised effective date and notify active members by email.
                </Section>

                <Section title="10. Contact">
                    Questions? <a href="mailto:contact@nexuscreatorlab.com" className="text-electric-blue">contact@nexuscreatorlab.com</a>
                </Section>

                <p className="text-xs text-muted-foreground italic mt-16 border-t border-white/10 pt-6">
                    This Privacy Policy is provided as a starting template and does not constitute legal advice. Consult a licensed attorney before final publication, especially for GDPR/CCPA compliance.
                </p>
            </article>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-10">
            <h2 className="font-heading text-xl font-bold text-electric-blue mb-3 tracking-tight">{title}</h2>
            <div className="text-sm text-foreground/85 leading-relaxed space-y-3">{children}</div>
        </section>
    );
}
